import { getErrorMessage, logMessage } from '../utils/helpers';
// CAS per-symbol session. All of the logic lives in session-source.ts so it can
// be tested without a browser; this class only wires it to the HTTP requester and
// to resolveSymbol. See that module's header for the why.
import { SessionSource, isCasSegment } from './session-source';
// @ts-ignore — vendored JS, kept byte-identical to mobile_charts by
// scripts/sync-vendored.sh (run --check in CI).
import { withSubsessions } from './vendor/session-timeline';
import type { SessionEntry, SessionHttpMeta } from './session-source';
import { HistoryProvider, } from './history-provider';
import { subscribeOnOdinStream, unsubscribe, closeConnection, openWebSocketConnection, closeConnectionTemporarily, extendedQuoteFieldsForSymbol } from './streaming_provider';
import { customIndicator } from "../components/TVChartContainer/custom_studies";
import { ChartDefaultConfiguration, News, NewsItem, GetQuotes, DepthData, MarksResponse, TimescaleMark, SearchResult } from "../models/charts"
import { Quotes, ResolveSymbolInfo, SymbolToTokenMap, TokenInfo } from '../models/symbolData';
import { PeriodParams, Bars } from "../models/historical"
import Subscribed from '../models/symbolData';
import { ScripSearchedData } from "../models/symbolData"
import { MarketDepth } from '../models/future';
import { trace, context, propagation, SpanStatusCode, TraceAPI } from "@opentelemetry/api";
import { Tracer } from '@opentelemetry/api';
import { globalSearch, Hit, processScripSearchedData } from '../models/globalSearch';
import { FeedUiController } from '../components/TVChartContainer/title_sync';
import { LastBarsCache, HistoryTailCache, lastBarKey, anyLastBarFor } from './bar-cache';
// function extractField(data, field, arrayIndex) {
//     const value = data[field];
//     return Array.isArray(value) ? value[arrayIndex] : value;
// }
/**
 * This class implements interaction with UDF-compatible datafeed.
 * See UDF protocol reference at https://github.com/tradingview/charting_library/wiki/UDF
 */

export class UDFCompatibleDatafeedBase {
    private _tracer: Tracer;
    private _configuration: ChartDefaultConfiguration;
    private _odinUrl: string;
    private _inHouseUrl: string;
    private _requester: any;
    private _odinRequester: any;
    private _inHouseRequester: any;
    private _historyProvider: HistoryProvider;
    public _symbolToTokenMap: Record<string, TokenInfo>;
    private _tokenToSymbolMap: Record<number, Record<number, string>>;
    private _symbolInfoMap: Record<string, ResolveSymbolInfo>;
    private supported_resolutions: string[];
    private _headerSymbol: string | null = null;
    // In-flight request maps: concurrent requests for the same symbol/token get
    // the SAME promise instead of each firing their own HTTP call. Entries are
    // removed once settled; failures are not cached so retries still work.
    private _symbolResolveInFlight: Record<string, Promise<ResolveSymbolInfo>> = {};
    private _tokenResolveInFlight: Record<string, Promise<TokenInfo>> = {};
    // Per-scrip metadata captured at seed time (watchlist StockData / global
    // search _source), keyed by canonical symbol. tick is stored in RUPEES
    // (callers must normalize; the raw APIs return paise). Lets quote/stream
    // paths skip /aux/symbol entirely, and — combined with the per-segment
    // static config below — lets resolveSymbol build a full LibrarySymbolInfo
    // locally for cash-equity scrips.
    private _seededScripMeta: Record<string, { description?: string; tick?: number; lotSize?: number; series?: string; isin?: string; hasIcon?: boolean }> = {};
    // Per-segment static config (session, holidays, corrections, flags, ...)
    // from ONE boot fetch of aux/symbol_segments. Port of mobile_charts
    // feat/segments-bulk-cache (4dd53ed), adapted: on the terminal this cache
    // is only trusted for segment-level truths; per-scrip fields (tick, lot,
    // description) must come from seeded scrip metadata, never from segment
    // defaults — wrong tick metadata on a live order path is not acceptable.
    private _segmentInfoCache: Record<string, any> | null = null;
    private _segmentFetchInFlight: Promise<void> | null = null;
    // Short-TTL dedupe for identical quote GETs (tab-title sync, watchlist refresh
    // and boot-time re-renders can fire the same query many times per second).
    private _quoteFetchCache: Record<string, { ts: number; promise: Promise<GetQuotes> }> = {};
    // One prefetch of aux/time at construction; served adjusted by elapsed
    // wall-clock seconds. Cleared on failure so getServerTime falls back to a
    // fresh fetch.
    private _serverTimePrime: { promise: Promise<number>; fetchedAtMs: number } | null = null;
    // CDN base for scrip logos — the same convention the app already uses in
    // models/globalSearch.ts. Named here so it's not a scattered magic literal.
    private static readonly STOCK_LOGO_BASE = "https://static.rupeezy.in/stock_images/svgs/";
    constructor(inhouse_url: string, requester: any, odinRequester: any, inHouseRequester: any) {
        this._tracer = trace.getTracer("vortex-tracer");
        this._configuration = defaultConfiguration();
        this._odinUrl = inhouse_url;
        this._inHouseUrl = inhouse_url + "aux/";
        this._requester = requester;
        this._odinRequester = odinRequester;
        this._inHouseRequester = inHouseRequester;
        this._historyProvider = new HistoryProvider(inhouse_url, this._requester);
        this._symbolToTokenMap = {}
        this._tokenToSymbolMap = {}
        this._symbolInfoMap = {}
        this.supported_resolutions = []
        this._fill_resolutions()
        // Warm the per-segment static config in the background (one GET per
        // boot). Failure is harmless: local symbol building just stays off and
        // every resolve falls through to /aux/symbol as before.
        this._ensureSegmentInfo()
        // Prime the server-time fetch too: TV asks for it mid-boot, previously
        // paying a serial ~150ms round trip between layout load and the first
        // resolve. Fired here it overlaps widget init; getServerTime serves the
        // primed value adjusted for elapsed wall-clock time.
        this._primeServerTime()
        this._setupWithConfiguration({
            "exchanges": [
                {
                    "value": "All Exchanges",
                    "name": "All Exchanges",
                },
                {
                    "value": "NSE",
                    "name": "NSE",
                },
                {
                    "value": "BSE",
                    "name": "BSE",
                },
                {
                    "value": "MCX",
                    "name": "MCX",
                }
            ],
            "symbols_types": [{ "name": "stocks", "value": 1 }],
            "supported_resolutions": [
                "5s",
                "15s",
                "30s",
                "1",
                "2",
                "3",
                "4",
                "5",
                "10",
                "15",
                "30",
                "45",
                "60",
                "120",
                "240",
                // "3",
                // "5",
                // "15",
                // "60",
                // "120",
                // "240",
                "1D",
                "1W",
                "1M"
            ],
            "supports_search": true,
            "supports_group_request": false,
            "supports_marks": false,
            "supports_timescale_marks": true,
            "supports_time": true
        });
    }

    _fill_resolutions() {
        // v32: the resolution menu renders EVERYTHING in supported_resolutions.
        // The old code declared the universe (every minute 1..1440, every 1..365D,
        // etc.) so that v26's "add custom interval" would validate any input —
        // v26's menu never displayed the full list, v32's does (the 538..1440-
        // minutes menu bug). Curate instead: standard TV steps + the intraday
        // multiples actually used in Indian F&O practice (75/125/375 etc.).
        // Trade-off: custom intervals OUTSIDE this list can no longer be created,
        // and old saved layouts on exotic intervals will get v32's built-in
        // "switch to a supported resolution" prompt instead of loading silently.
        this.supported_resolutions.push(
            "5S", "15S", "30S",
            "1", "2", "3", "4", "5", "10", "15", "25", "30", "45",
            "60", "75", "120", "125", "180", "240", "375",
            "1D", "2D", "3D", "5D",
            "1W", "2W",
            "1M", "3M", "6M", "12M",
        )
    }

    OnReadyCallback(callback: (a: ChartDefaultConfiguration) => void) {
        setTimeout(() => callback(defaultConfiguration()));
    }
    onReady(callback: (a: ChartDefaultConfiguration) => void) {
        setTimeout(() => callback(defaultConfiguration()));
    }
    connectFeed(ocToken: string, clientCode: string) {
        openWebSocketConnection(ocToken, clientCode);
    }
    closeFeed() {
        closeConnection();
    }
    closeFeedTemporarily() {
        closeConnectionTemporarily();
    }

    getNews(symbol: string, onDataCallback: ({ }) => void) {
        const tracer = trace.getTracer("vortex-tracer");
        return tracer.startActiveSpan("GetNews", async (span) => {
            for (const key in this._symbolInfoMap) {
                var values = key.split(":")
                if (values.length > 1 && values[1] == symbol) {
                    symbol = key;
                    break;
                }
            }
            this._inHouseRequester.sendRequest(this._inHouseUrl, 'newsBySymbol/' + symbol, undefined, undefined, "GET")
                .then((res: News) => {
                    var body = res.response.map((item: NewsItem) => {
                        return {
                            title: item.title,
                            published: Date.parse(item.pubDate),
                            // v32: the News widget requires `provider: { id, name }` and
                            // crashes rendering items without it (reads provider.name).
                            // Pre-v28 accepted a plain `source` string — keep both.
                            provider: {
                                id: String(item.source || 'news').toLowerCase().replace(/[^a-z0-9]+/g, '-'),
                                name: item.source || 'News',
                            },
                            source: item.source,
                            shortDescription: '',
                            link: item.newsLink,
                            fullDescription: ''
                        }

                    })
                    onDataCallback({ title: "News", newsItems: body });
                }).catch((e: any) => {
                    context.with(trace.setSpan(context.active(), span), () => {
                        console.error("news api request error", e)
                    });
                }).finally(() => {
                    span.end()
                });
        });

    }


    _exchangeMap(segment: string) {
        const dict: Record<string, string> = {
            "2": "NSE_FO",
            "13": "NSE_CD",
            "1": "NSE_EQ",
            "5": "MCX_FO",
            "4": "BSE_FO",
            "3": "BSE_EQ"
        }
        return dict[segment]
    }

    //This is being used to get the quotes of symbols which all are in watchlist and on screen chart like ltp,ch,chp,oi etc. used by tradingview library
    async getQuotes(symbols: string[], onDataCallback: (a: Quotes[]) => void, onErrorCallback: (a: any) => void) {
        return this._tracer.startActiveSpan("GetQuotes", async (span) => {
            var input_string: string = ""
            var quoteSymbolMap: Record<string, ResolveSymbolInfo> = {}
            if (symbols == undefined || symbols.length == 0) {
                span.setStatus({ code: SpanStatusCode.ERROR });
                span.end()
                onErrorCallback("No symbols found")
                return
            }
            for (let index = 0; index < symbols.length; index++) {
                const symbol = symbols[index];
                // Identity fields are enough for a quote — serve them from the
                // seeded caches (watchlist/search ingress) and skip /aux/symbol.
                const seededInfo = this._quoteInfoFromSeed(symbol);
                if (seededInfo) {
                    quoteSymbolMap[this._exchangeMap(String(seededInfo.odin_market_segment_id)) + "-" + seededInfo.odin_token] = seededInfo
                    input_string = input_string + "q=" + this._exchangeMap(String(seededInfo.odin_market_segment_id)) + "-" + seededInfo.odin_token + "&"
                    continue
                }
                // Identity only — no session. TV's watchlist quotes every row it
                // shows, and each miss here used to cost a session_history lookup
                // for a symbol that is not being drawn as candles.
                await this.resolveSymbolInfo(symbol, () => { }, () => { }, 'GET')?.then((symbolInfo: ResolveSymbolInfo) => {
                    quoteSymbolMap[this._exchangeMap(String(symbolInfo.odin_market_segment_id)) + "-" + symbolInfo.odin_token] = symbolInfo
                    input_string = input_string + "q=" + this._exchangeMap(String(symbolInfo.odin_market_segment_id)) + "-" + symbolInfo.odin_token + "&"
                })
            }
            input_string = input_string + "mode=full"
            this._dedupedQuoteFetch(input_string)
                .then((response: GetQuotes) => {
                    var quotes: Quotes[] = []
                    if (response.data == undefined) {
                        throw new Error("quotes api request error " + JSON.stringify(symbols) + " " + input_string + JSON.stringify(response))
                    }
                    var keys = Object.keys(response.data)
                    for (let index = 0; index < keys.length; index++) {
                        const item = response.data[keys[index]];
                        const bidPrice: number = item.depth.buy.find((obj: DepthData) => obj.hasOwnProperty("price"))?.["price"] as number
                        const askPrice: number = item.depth.sell.find((obj: DepthData) => obj.hasOwnProperty("price"))?.["price"] as number
                        const symbolInfo = quoteSymbolMap[keys[index]]
                        let body: Subscribed = {
                            ch: parseFloat((item.last_trade_price - item.close_price).toFixed(2)),
                            chp: parseFloat(((item.last_trade_price - item.close_price) / item.close_price * 100).toFixed(2)),
                            short_name: String(symbolInfo.odin_symbol),
                            exchange: symbolInfo.odin_exchange == 'NSE_EQ' ? 'NSE' : String(symbolInfo.odin_exchange),
                            description: String(symbolInfo.description),
                            lp: item.last_trade_price,
                            open_price: item.open_price,
                            high_price: item.high_price,
                            low_price: item.low_price,
                            prev_close_price: item.close_price,
                            volume: item.volume ? item.volume : 0,
                            ask: askPrice ? askPrice : 0,
                            bid: bidPrice ? bidPrice : 0,
                            original_name: symbolInfo.name,
                            dpr: item.dpr_low && item.dpr_high ? item.dpr_low / 100 + "-" + item.dpr_high / 100 : "0.00-0.00",
                            placeOrder: true,
                            // Pre/post-market marker. Contributes `rtc` only when
                            // last_trade_time proves the price was struck outside
                            // regular hours, and never asserts current_session —
                            // this path does not own the session. See
                            // extendedQuoteFieldsForSymbol for both reasons.
                            ...extendedQuoteFieldsForSymbol(
                                symbolInfo, Date.now(), item.last_trade_price,
                                item.close_price > 0 ? item.close_price : 0,
                                undefined, undefined,
                                item.last_trade_time > 0 ? item.last_trade_time * 1000 : undefined),
                        }
                        let quote: Quotes = {
                            n: String(symbolInfo.full_name),
                            s: "ok",
                            status: "ok",
                            symbolname: symbolInfo.full_name,
                            v: body
                        }
                        quotes.push(quote)
                    }
                    onDataCallback(quotes)
                }).catch((e: any) => {
                    span.setStatus({ code: SpanStatusCode.ERROR });
                    span.setAttribute("error", e)
                    console.error("quotes api request error", e)
                    onErrorCallback(e)
                }).finally(() => {
                    span.end()
                })
        })

    }


    // Identical quote queries fired within a short window (tab-title sync on
    // symbol-change, watchlist refresh, boot re-renders) share one HTTP GET.
    // 1.5s is well under quote staleness tolerance — live prices ride the
    // websocket, this endpoint only primes the snapshot.
    private _dedupedQuoteFetch(queryString: string): Promise<GetQuotes> {
        const now = Date.now();
        const cached = this._quoteFetchCache[queryString];
        if (cached && (now - cached.ts) < 1500) {
            return cached.promise;
        }
        const request: Promise<GetQuotes> = this._inHouseRequester.getQuotes(this._inHouseUrl, queryString);
        const entry = { ts: now, promise: request };
        this._quoteFetchCache[queryString] = entry;
        // Self-prune: the entry is only useful within the 1.5s dedup window, so
        // drop it shortly after it settles — otherwise the map would grow
        // unbounded over a long session as a user views many distinct symbols.
        // Guard on identity so a newer request for the same query isn't deleted.
        const cleanup = () => { if (this._quoteFetchCache[queryString] === entry) delete this._quoteFetchCache[queryString]; };
        request.then(() => { setTimeout(cleanup, 1500); }, () => { cleanup(); });
        return request;
    }

    // Quote-grade symbol info assembled locally from the seeded caches — the
    // quote/stream paths only need identity fields (token, segment, names), not
    // the full /aux/symbol payload (session, pricescale, ...). Returns the full
    // resolved info when available, a seeded stub when we have token+segment in
    // hand, or null (caller falls back to resolveSymbol HTTP). The stub is NOT
    // written to _symbolInfoMap: the main chart series must keep resolving the
    // full-fidelity payload.
    private _quoteInfoFromSeed(symbolName: string): ResolveSymbolInfo | null {
        const resolved = this._symbolInfoMap[symbolName];
        if (resolved) return resolved;
        const tok = this._symbolToTokenMap[symbolName];
        if (!tok || tok.token == null || tok.market_segment_id == null) return null;
        const segment = Number(tok.market_segment_id);
        const bare = symbolName.indexOf(':') !== -1 ? String(symbolName.split(':').pop()) : symbolName;
        const meta = this._seededScripMeta[symbolName] || {};
        return {
            symbol: symbolName,
            ticker: symbolName,
            full_name: symbolName,
            name: symbolName,
            description: meta.description || bare,
            odin_symbol: bare,
            odin_token: String(tok.token),
            odin_market_segment_id: segment,
            odin_exchange: this._exchangeMap(String(segment)),
        };
    }

    //Subscribe the quotes like ltp,oi,change,change% etc
    subscribeTtQuotes(symbols: string[], fastSymbols: string[] | null, onRealtimeCallback: () => void, listenerGuid: string) {
        if (fastSymbols == null) {
            fastSymbols = symbols;
        }

        const allSymbols = Array.from(new Set([...fastSymbols, ...symbols]));
        const symbolInfos: Record<string, ResolveSymbolInfo> = {};
        const onInfoReady = (symbol: string, symbolInfo: ResolveSymbolInfo) => {
            symbolInfos[symbol] = symbolInfo;
            if (Object.keys(symbolInfos).length === allSymbols.length) {
                subscribeOnOdinStream(
                    Object.values(symbolInfos),
                    undefined,
                    onRealtimeCallback,
                    listenerGuid,
                    () => { },
                    undefined,
                    'tt-quotes'
                );
            }
        };

        allSymbols.forEach((symbol: string) => {
            // The stream only needs token/segment identity — serve from the
            // seeded caches when possible, no /aux/symbol round-trip.
            const seededInfo = this._quoteInfoFromSeed(symbol);
            if (seededInfo) {
                onInfoReady(symbol, seededInfo);
                return;
            }
            // Identity only — the stream needs token/segment, never a session.
            this.resolveSymbolInfo(symbol, (symbolInfo: ResolveSymbolInfo) => {
                onInfoReady(symbol, symbolInfo);
            },
                () => { },
                ''
            );
        });
    }


    //Subscribe the quotes like ltp,oi,change,change% etc used by tradingview library
    subscribeQuotes(symbols: string[], fastSymbols: string[], onRealtimeCallback: () => void, listenerGuid: string) {
        if (fastSymbols == null) {
            fastSymbols = symbols
        }
        var symbolInfos: Record<string, ResolveSymbolInfo> = {}
        const onInfoReady = (symbol: string, symbolInfo: ResolveSymbolInfo) => {
            symbolInfos[symbol] = symbolInfo
            if (Object.keys(symbolInfos).length == symbols.length) {
                subscribeOnOdinStream(Object.values(symbolInfos),
                    undefined,
                    onRealtimeCallback,
                    listenerGuid,
                    () => { },
                    undefined, 'quotes')
            }
        };
        symbols.forEach((symbol: string) => {
            // Seeded identity is enough for the stream subscription.
            const seededInfo = this._quoteInfoFromSeed(symbol);
            if (seededInfo) {
                onInfoReady(symbol, seededInfo);
                return;
            }
            // Identity only — see the note in subscribeQuotes.
            this.resolveSymbolInfo(symbol, (symbolInfo: ResolveSymbolInfo) => {
                onInfoReady(symbol, symbolInfo)
            }, () => { }, '')
        });


    }
    //Subscribe from scrip tokens for quotes like ltp,oi,change,change% etc
    async subcscribeQuotesFromTokens(tokens: TokenInfo[], onRealtimeCallback: (a: Quotes[]) => void, listenerGuid: string) {
        const promises: ResolveSymbolInfo[] = await Promise.all(tokens.map(async (token: TokenInfo) => {
            const info = await this.tokenInfo(token);
            return {
                symbol: info.symbol,
                odin_token: info.token,
                odin_market_segment_id: info.market_segment_id
            };
        }));
        subscribeOnOdinStream(promises, undefined, onRealtimeCallback, listenerGuid, () => { }, undefined, 'quotes');

    }
    //To unsubscribe the quotes from odin
    unsubscribeQuotes(listenerGuid: string | undefined) {
        unsubscribe(String(listenerGuid))
    }

    getMarks(symbolInfo: ResolveSymbolInfo, from: number, to: number, onDataCallback: (a: TimescaleMark[]) => {}, resolution: string) {
        if (!this._configuration.supports_marks) {
            return;
        }
        return this._tracer.startActiveSpan("GetMarks", async (span) => {

            const requestParams = {
                symbol: symbolInfo.ticker || '',
                from: from,
                to: to,
                resolution: resolution,
                market_segment_id: symbolInfo.odin_market_segment_id,
                token: symbolInfo.odin_token
            };
            this._inHouseSend('timescaleMarks', requestParams, {}, "GET")
                .then((response: MarksResponse) => {
                    if (response.marks) {
                        onDataCallback(response.marks)
                    } else {
                        onDataCallback([])
                    }
                })
                .catch((error: any) => {
                    span.setStatus({ code: SpanStatusCode.ERROR });
                    span.setAttribute("error", getErrorMessage(error))
                    logMessage(`UdfCompatibleDatafeed: Request marks failed: ${getErrorMessage(error)}`);
                    onDataCallback([]);
                }).finally(() => {
                    span.end()
                });
        })

    }
    getTimescaleMarks(symbolInfo: ResolveSymbolInfo, from: number, to: number, onDataCallback: (a: TimescaleMark[]) => void, resolution: string) {
        if (!this._configuration.supports_timescale_marks) {
            return;
        }
        return this._tracer.startActiveSpan("GetTimescaleMarks", async (span) => {
            const requestParams = {
                symbol: symbolInfo.symbol || '',
                from: from,
                to: to,
                resolution: resolution,
                market_segment_id: symbolInfo.odin_market_segment_id,
                token: symbolInfo.odin_token
            };
            this._inHouseSend('timescaleMarks', requestParams, {}, "GET")
                .then((response: MarksResponse) => {
                    if (response.marks) {
                        onDataCallback(response.marks)
                    } else {
                        onDataCallback([])
                    }
                    span.end()
                })
                .catch((error: any) => {
                    span.setStatus({ code: SpanStatusCode.ERROR });
                    span.setAttribute("error", getErrorMessage(error))
                    span.end()
                    logMessage(`UdfCompatibleDatafeed: Request timescale marks failed: ${getErrorMessage(error)}`);
                    onDataCallback([]);
                }).finally(() => {
                    span.end()
                });
        })

    }

    private _primeServerTime() {
        const fetchedAtMs = Date.now();
        const promise = this._send('time').then((response: number) => Number(response));
        this._serverTimePrime = { promise, fetchedAtMs };
        promise.then(
            (t: number) => { if (isNaN(t)) { this._serverTimePrime = null; } },
            () => { this._serverTimePrime = null; }
        );
    }

    getServerTime(callback: (a: number) => void) {
        if (!this._configuration.supports_time) {
            return;
        }
        const prime = this._serverTimePrime;
        if (prime) {
            return prime.promise.then((serverSeconds: number) => {
                const adjusted = serverSeconds + Math.round((Date.now() - prime.fetchedAtMs) / 1000);
                if (!isNaN(adjusted)) {
                    callback(adjusted);
                    return;
                }
                return this._fetchServerTime(callback);
            }).catch(() => this._fetchServerTime(callback));
        }
        return this._fetchServerTime(callback);
    }

    private _fetchServerTime(callback: (a: number) => void) {
        return this._tracer.startActiveSpan("GetServerTime", async (span) => {
            return this._send('time')
                .then((response: number) => {
                    const time = response;
                    if (!isNaN(time)) {
                        callback(time);
                    } else {
                        span.setStatus({ code: SpanStatusCode.ERROR });
                        span.setAttribute("error", "Invalid server time")
                    }
                })
                .catch((error: any) => {
                    span.setStatus({ code: SpanStatusCode.ERROR });
                    span.setAttribute("error", getErrorMessage(error))
                    logMessage(`UdfCompatibleDatafeed: Fail to load server time, error=${getErrorMessage(error)}`);
                }).finally(() => {
                    span.end()
                });
        })

    }

    //Search the symbols based on userInput using odinApi
    searchSymbols(userInput: string, exchange: string, symbolType: number, onResult: (a: ScripSearchedData[]) => void, page: number | string = 1) {
        // v32: since library v29.4 TradingView passes a 5th argument `searchSource`
        // (a string such as "symbolSearch" or "compare") to searchSymbols. Our
        // optional `page` parameter occupied that slot, so the string ended up in
        // the globalSearch URL as page_no=symbolSearch -> 400. Coerce: only a real
        // page number (internal pagination callers) survives; anything else -> 1.
        const pageNo: number = (typeof page === 'number' && isFinite(page) && page >= 1) ? page : 1;
        return this._tracer.startActiveSpan("GetServerTime", async (span) => {
            if (this._configuration.supports_search) {
                // var final = 23;
                // switch(exchange){
                //     case "NSE": {
                //         switch(symbolType){
                //             case 5: {
                //                 final = 3
                //                 break; 
                //             }
                //             case 4: {
                //                 final = 1
                //                 break; 
                //             }
                //             case 3: {
                //                 final = 2
                //                 break; 
                //             }
                //             case 2: {
                //                 final = 2
                //                 break; 
                //             }
                //             case 1: { 
                //                 final  =1 
                //                 break; 
                //             }
                //         }
                //         break; 
                //     }
                //     case "BSE": {
                //         switch(symbolType){
                //             case 5: {
                //                 final = 12
                //                 break; 
                //             }
                //             case 4: {
                //                 final = 8
                //                 break; 
                //             }
                //             case 3: {
                //                 final = 4
                //                 break; 
                //             }
                //             case 2: {
                //                 final = 4
                //                 break; 
                //             }
                //             case 1: { 
                //                 final  =8 
                //                 break; 
                //             }
                //             default: {
                //                 final = 12
                //             }
                //         }
                //         break; 
                //     }
                //     case "MCX": {
                //         final = 16 
                //         break;
                //     }
                //     case "All Exchanges": {
                //         switch(symbolType){
                //             case 6: {
                //                 final = 9
                //                 break; 
                //             }
                //             case 5: {
                //                 final = 23
                //                 break; 
                //             }
                //             case 4: {
                //                 final = 9
                //                 break; 
                //             }
                //             case 3: {
                //                 final = 22
                //                 break; 
                //             }
                //             case 2: {
                //                 final = 22
                //                 break; 

                //             }
                //             case 1: { 
                //                 final  = 9
                //                 break; 
                //             }
                //         }
                //         break; 
                //     }
                // }
                let asset_type: string;

                switch (symbolType) {
                    case 1:
                        asset_type = 'EQUITY';
                        break;
                    case 2:
                        asset_type = 'OPTION';
                        break;
                    case 3:
                        asset_type = 'FUTURE';
                        break;
                    case 4:
                        asset_type = 'INDICES';
                        break;
                    default:
                        asset_type = '';
                }
                ((userInput) => {
                    const params = new URLSearchParams({
                        query: (userInput.split(":")[1] ?? userInput).toUpperCase(),
                        page_no: String(pageNo),
                        page_size: String(100),
                        index_name: 'stock_idx'
                    });

                    if (exchange !== 'All Exchanges') {
                        params.append('exchange', exchange);
                    }

                    if (asset_type === 'INDICES') {
                        params.append('instrument_name', 'EQIDX');
                    } else {
                        params.append('asset_type', asset_type);
                    }

                    this._odinSend(`aux/globalSearch?${params.toString()}`)
                        .then((response: globalSearch) => {
                            if (!response.status) {
                                span.setAttribute("error", "search symbol error")
                                span.setStatus({ code: SpanStatusCode.ERROR });

                                logMessage(`UdfCompatibleDatafeed: search symbols error`);
                                onResult([]);
                                return;
                            }
                            let data: ScripSearchedData[] = response.data.hits.hits
                                .map((item: Hit) => {
                                    const processed = processScripSearchedData(item, exchange, symbolType);
                                    // Seed caches from the search payload — a picked result
                                    // then resolves with zero HTTP. Restricted to EQUITIES /
                                    // EQIDX: their constructed tickers ("NSE:RELIANCE",
                                    // "NSE:NIFTYIDX") match the canonical form. F&O tickers
                                    // are built from security_desc and are NOT safe to seed
                                    // (same lesson as webapp ADVA-20 54762c3a).
                                    try {
                                        const src = item && item._source;
                                        const instrument = src && src.instrument_name;
                                        if (processed && processed.ticker && src && src.token && src.market_segment_id && (instrument === 'EQUITIES' || instrument === 'EQIDX')) {
                                            this.seedTokenSymbol({
                                                token: src.token,
                                                market_segment_id: src.market_segment_id === 8 ? 3 : src.market_segment_id,
                                                symbol: String(processed.ticker),
                                                description: src.security_desc,
                                                // search returns tick in paise, same as the watchlist API
                                                tick: (typeof src.tick === 'number' && isFinite(src.tick)) ? src.tick / 100 : undefined,
                                                lotSize: src.lot_size,
                                                series: src.series,
                                                isin: src.isin_code,
                                                hasIcon: src.has_icon,
                                            });
                                        }
                                    } catch (e) { /* seeding is best-effort */ }
                                    return processed;
                                })
                                .filter((e: ScripSearchedData | null): e is ScripSearchedData => e !== null && e["full_name"] !== undefined);
                            onResult(data);
                        })
                        .catch((reason: any) => {
                            span.setStatus({ code: SpanStatusCode.ERROR });
                            span.end()
                            span.setAttribute("error", getErrorMessage(reason))
                            logMessage(`UdfCompatibleDatafeed: Search symbols for '${userInput}' failed. Error=${getErrorMessage(reason)}`);
                            onResult([]);
                        }).finally(() => {
                            span.end()
                        });
                })(userInput);

            }
        })
    }

    // TradingView's datafeed entry point. Anything TV resolves can end up rendered
    // as candles — the chart series, a compare/overlay series, a study's symbol
    // input — and TV caches every resolved symbolInfo for the life of the page
    // (see _withSessionHistory), so these MUST carry the CAS session.
    resolveSymbol(symbolName: string, onResolve: (a: ResolveSymbolInfo) => void, onError: (a: string) => void, extension?: any) {
        return this._resolveSymbol(symbolName, onResolve, onError, true);
    }

    // App-side symbol lookup: order rows, positions, baskets, the option/future
    // chains, the buy/sell panel. Identical to resolveSymbol minus the session
    // lookup, because none of it is drawn as candles.
    //
    // Skipping it is safe, not a shortcut. These calls bypass TradingView entirely,
    // so they never populate ITS symbol cache; the payload lands in our own
    // _symbolInfoMap, and if the chart later shows that symbol, TV's resolve hits
    // that memo and runs _withSessionHistory over it then. The session is applied
    // to a COPY per resolve, which is what makes the same memo serve both callers
    // correctly. `extension` is accepted and ignored so call sites need no edit.
    resolveSymbolInfo(symbolName: string, onResolve: (a: ResolveSymbolInfo) => void, onError: (a: string) => void, extension?: any) {
        return this._resolveSymbol(symbolName, onResolve, onError, false);
    }

    private _resolveSymbol(symbolName: string, onResolve: (a: ResolveSymbolInfo) => void, onError: (a: string) => void, applySession: boolean) {
        logMessage('UdfCompatibleDatafeed: Resolve requested');
        // const currencyCode = extension && extension.currencyCode;
        // const unitId = extension && extension.unitId;
        const resolveRequestStartTime = Date.now();
        // Arrow (was a function declaration) so `this` reaches the CAS session
        // lookup below. All four resolve branches — memo-cache hit, local build,
        // in-flight piggyback and HTTP — funnel through here, which makes it the
        // one place the per-symbol session has to be applied.
        const onResultReady = (symbolInfo: ResolveSymbolInfo, supported_resolutions: string[]) => {
            logMessage(`UdfCompatibleDatafeed: Symbol resolved: ${Date.now() - resolveRequestStartTime}ms`);
            if (onResolve != undefined && onResolve != null && typeof onResolve == 'function') {
                symbolInfo.has_seconds = true
                symbolInfo.supported_resolutions = supported_resolutions
                symbolInfo.seconds_multipliers = ["1"]
                symbolInfo.has_ticks = true
                // Server-native intraday aggregations (verified against
                // /data/history on NSE equity/index/F&O and MCX, 2026-07-20).
                // Declaring them makes v32 fetch e.g. 60m directly (~400 bars,
                // ~200ms) instead of aggregating 1h client-side from ~26k
                // one-minute bars (~1.6s). 25/75/125/375m are NOT server-native
                // (server returns junk) — TV derives them from 5m/15m here.
                // Every value divides the 1440-min day, so the stream-side
                // N-minute bucketing grid keeps its session phase across days
                // (see the generic minute branch in streaming_provider).
                symbolInfo.intraday_multipliers = ['1', '2', '3', '4', '5', '10', '15', '30', '45', '60', '120', '180', '240']
                // An app-side lookup takes the symbol as-is: no session, no wait,
                // no request. See resolveSymbolInfo.
                if (!applySession) { onResolve(symbolInfo); return; }
                // CAS: resolve the per-symbol, per-date session before handing the
                // symbol to TV. TV memoises symbolInfo internally and cannot be made
                // to re-read `session` afterwards without recreating the widget
                // (verified against v32), so this is the only chance we get.
                // Bounded and fail-open — a slow or dead endpoint costs at most the
                // timeout and falls through to the segment default, i.e. today's
                // behaviour. The override is applied to a COPY: `symbolInfo` is the
                // same object stored in `_symbolInfoMap`, and writing the session
                // into it would make a cold-cache first resolve sticky forever.
                // withSubsessions LAST, once the final session is known: the
                // pre/post windows are derived from it. TradingView computes
                // `current_session` itself and can only report pre/post-market
                // for a symbol that declares subsessions — without them the
                // price marker can never appear. Non-CAS segments pass through
                // _withSessionHistory untouched and still need this, so it wraps
                // the result rather than living inside it.
                this._withSessionHistory(symbolInfo, symbolName)
                    .then((finalInfo) => onResolve(withSubsessions(finalInfo, Date.now())));
            }
        }
        const params = {
            symbol: symbolName,
        };
        if (!this._configuration.supports_group_request) {
            if (this._symbolInfoMap[symbolName]) {
                setTimeout(() => onResultReady(this._symbolInfoMap[symbolName], this.supported_resolutions));
                return Promise.resolve(this._symbolInfoMap[symbolName])
            }
            // Cash-equity scrips with seeded metadata + segment config resolve
            // entirely locally — zero HTTP (see _buildSymbolInfoLocal for the
            // gating rationale). Cached like an HTTP result: full fidelity.
            const localInfo = this._buildSymbolInfoLocal(symbolName);
            if (localInfo) {
                this._symbolInfoMap[symbolName] = localInfo;
                logMessage(`UdfCompatibleDatafeed: Symbol resolved locally (seeded + segment config): ${symbolName}`);
                setTimeout(() => onResultReady(localInfo, this.supported_resolutions));
                return Promise.resolve(localInfo);
            }
            // A resolve for this symbol is already on the wire — piggyback on it
            // instead of firing a duplicate /aux/symbol (boot fires several
            // resolves for the chart symbol before the first response lands).
            const pendingResolve = this._symbolResolveInFlight[symbolName];
            if (pendingResolve) {
                return pendingResolve.then((symbolInfo: ResolveSymbolInfo) => {
                    onResultReady(symbolInfo, this.supported_resolutions);
                    return symbolInfo;
                }, (reason: any) => {
                    // Mirror the direct path's semantics: onError only for a
                    // real unknown-symbol response; transient failures just
                    // propagate (same as the original network-error path).
                    if (reason === 'unknown_symbol' && onError != undefined && typeof onError == 'function') {
                        onError('unknown_symbol');
                    }
                    return Promise.reject(reason);
                });
            }
            const resolveRequest = this._tracer.startActiveSpan("ResolveSymbol", async (span) => {
                return this._inHouseSend('symbol', params).then((response: ResolveSymbolInfo) => {
                    if (response.s) {
                        logMessage(`UdfCompatibleDatafeed: Symbol resolved error: Unknown symbol`);
                        if (onError != undefined) {
                            onError('unknown_symbol');
                        }
                        span.setStatus({ code: SpanStatusCode.ERROR });
                        span.setAttribute("error", "unknown_symbol")
                        return Promise.reject('unknown_symbol')
                    }
                    else {
                        if (response.odin_market_segment_id == 8 || String(response.odin_market_segment_id) == "8") {
                            response.odin_market_segment_id = 3
                        }
                        this._symbolInfoMap[symbolName] = response
                        onResultReady(response, this.supported_resolutions);
                        return Promise.resolve(response)
                    }
                }).catch((reason: any) => {
                    span.setStatus({ code: SpanStatusCode.ERROR });
                    span.setAttribute("error", getErrorMessage(reason))
                    logMessage(`UdfCompatibleDatafeed: Symbol resolved error: ${getErrorMessage(reason)}`);
                    return Promise.reject(reason)
                }).finally(() => {
                    span.end()
                })
            })
            this._symbolResolveInFlight[symbolName] = resolveRequest;
            // Cleanup via a fully-handled derived chain (no unhandled-rejection
            // noise when the resolve fails and nobody piggybacked).
            resolveRequest.then(
                () => { delete this._symbolResolveInFlight[symbolName]; },
                () => { delete this._symbolResolveInFlight[symbolName]; }
            );
            return resolveRequest;
        }
    }

    //This returns the OHLCV of every candle i.e history
    getBars(symbolInfo: ResolveSymbolInfo, resolution: string, periodParams: PeriodParams, onResult: ({ }, { }) => void, onError: (a: string) => {}) {
        const getBarsRequestStartTime = Date.now();
        logMessage(`UdfCompatibleDatafeed: Get Bars started: ${symbolInfo.symbol}, ${resolution}, ${JSON.stringify(periodParams)}`);
        this._tracer.startActiveSpan("GetBarsUdf", async (span) => {
            // The custom indicator warms its own 1D + weekly bar mappings for a
            // NEW symbol (two /data/history fetches). Run that IN PARALLEL with
            // the actual candle fetch instead of ahead of it — previously the
            // real candles waited ~300ms for the indicator's warm-up to finish.
            // onResult still fires only when BOTH are ready, so the indicator
            // mapping is complete before TV computes studies (same guarantee as
            // the old serial order).
            // [TV32-DIAG] per-phase timing: which half gates first paint — the
            // indicator warm-up or the candle fetch. Enable: window.__tvDiag = true.
            const _t0 = Date.now();
            let _tInd = 0, _tBars = 0;
            const indicatorWarmup = customIndicator.getIndicatorValues(symbolInfo).then((r: any) => { _tInd = Date.now() - _t0; return r; });
            const barsRequest = this._historyProvider.getBars(symbolInfo, resolution, periodParams).then((r: any) => { _tBars = Date.now() - _t0; return r; });
            Promise.all([indicatorWarmup, barsRequest]).then(([_, result]: [any, any]) => {
                {
                    logMessage('UdfCompatibleDatafeed:' + ' ' + resolution + ' ' + JSON.stringify(periodParams));
                    // [TV32-DIAG] history shape + timing, all resolutions.
                    // Enable: window.__tvDiag = true.
                    if ((window as any).__tvDiag && result.bars.length) {
                        const _f = result.bars[0], _l = result.bars[result.bars.length - 1];
                        console.warn('[TV32-DIAG] getBars', resolution, symbolInfo.symbol,
                            'bars=', result.bars.length,
                            'totalMs=', Date.now() - _t0, '(indicatorMs=', _tInd, 'barsMs=', _tBars, ')',
                            'first=', new Date(_f.time).toISOString(),
                            'last=', new Date(_l.time).toISOString(),
                            'countback=', (periodParams as any).countBack,
                            'firstDataRequest=', periodParams.firstDataRequest);
                    }
                    if (result.bars.length) {
                        // Raise (never lower) the history-tail floor consulted by
                        // the stream clamp — see HistoryTailCache in bar-cache.ts.
                        {
                            const _tailKey = `${symbolInfo.full_name || symbolInfo.symbol}|${resolution}`;
                            const _tailTime = result.bars[result.bars.length - 1].time;
                            const _prevTailTime = HistoryTailCache.get(_tailKey);
                            if (typeof _tailTime === 'number' && (_prevTailTime === undefined || _tailTime > _prevTailTime)) {
                                HistoryTailCache.set(_tailKey, _tailTime);
                            }
                        }
                        if (periodParams.firstDataRequest) {
                            const lastBar = result.bars[result.bars.length - 1];
                            // Keyed WITH resolution: two charts on the same
                            // symbol at different resolutions are separate
                            // series and must not overwrite each other's last
                            // bar (see bar-cache.ts).
                            LastBarsCache.set(lastBarKey(symbolInfo.full_name, resolution), {
                                ...lastBar,
                            });
                            // Paint the browser TAB TITLE from the candles we just
                            // fetched — this is why setHeaderSymbol no longer fires
                            // its own /analytics/quote. Works market-closed too (the
                            // last candle carries the last traded price). Strictly
                            // guarded to the active header symbol, so nothing else is
                            // affected and TV's own data/getBars result is unchanged.
                            if (this._headerSymbol === symbolInfo.symbol && typeof lastBar.close === 'number') {
                                FeedUiController.setSymbol(symbolInfo.description ? String(symbolInfo.description) : String(symbolInfo.symbol));
                                FeedUiController.setPrice(lastBar.close);
                            }
                        }
                        logMessage(`UdfCompatibleDatafeed: Get Bars completed: ${symbolInfo.symbol}, ${resolution}, ${JSON.stringify(periodParams)},${JSON.stringify(result.bars)} : ${Date.now() - getBarsRequestStartTime}ms`);
                        onResult(result.bars, { noData: false });
                    } else {
                        logMessage(`UdfCompatibleDatafeed: Get Bars completed no data: ${symbolInfo.symbol}, ${resolution}, ${JSON.stringify(periodParams)}: ${Date.now() - getBarsRequestStartTime}ms`);
                        onResult(result.bars, { noData: true, nextTime: result.meta.nextTime });
                    }
                }
            }).catch((e: any) => {
                span.setStatus({ code: SpanStatusCode.ERROR });
                span.setAttribute("error", e)
                onError(e)
            }).finally(() => {
                span.end()
            })
        });


    }

    setHeaderSymbol(symbol: string) {
        this._headerSymbol = symbol;
        // FeedUiController drives only the browser TAB TITLE ("SYMBOL · ₹LTP").
        // We no longer fire a dedicated full quote (depth + bid/ask) here just for
        // the title — that was a redundant /analytics/quote on every symbol change.
        // The price is served from the chart's own candles instead:
        //   - if the active symbol's last bar is already cached, paint immediately
        //     (covers re-selecting a symbol whose chart is already loaded);
        //   - otherwise getBars() — which always runs for the active symbol, market
        //     open OR closed — paints the title the moment its first candles land.
        // subscribeBars() then keeps the price live on every stream tick. If we have
        // no price yet we intentionally leave the title as-is (the render() only
        // shows "SYMBOL · ₹LTP" once a price exists), so the title is never blanked.
        const info = this._symbolInfoMap[symbol] || this._quoteInfoFromSeed(symbol);
        // Any resolution will do here — this only paints the tab title with a
        // recent close, and the header symbol's series may be on any timeframe.
        const lastBar: any = info ? anyLastBarFor(info.full_name || symbol) : undefined;
        if (info && lastBar && typeof lastBar.close === 'number') {
            FeedUiController.setSymbol(info.description ? String(info.description) : String(info.name || symbol));
            FeedUiController.setPrice(lastBar.close);
        }
    }

    //Subscribe the bar to get data of candles in real time 
    subscribeBars(symbolInfo: ResolveSymbolInfo, resolution: string, onRealtimeCallback: (bar: any) => void, subscriberUID: string, onResetCacheNeededCallback: () => void) {
        logMessage(`UdfCompatibleDatafeed: Subscribe Bars called ${symbolInfo}, ${resolution}, ${subscriberUID}`);
        subscribeOnOdinStream([symbolInfo],
            resolution,
            (bar) => {
                if (this._headerSymbol == symbolInfo.symbol) {
                    FeedUiController.setSymbol(symbolInfo.description ? symbolInfo.description : symbolInfo.symbol);
                    FeedUiController.setPrice(bar.close);
                }
                onRealtimeCallback(bar);
            },
            subscriberUID,
            onResetCacheNeededCallback,
            LastBarsCache.get(lastBarKey(symbolInfo.full_name, resolution)), 'bar')
    }
    //Unsubscribe the bar
    unsubscribeBars(listenerGuid: string) {
        logMessage(`UdfCompatibleDatafeed: Unsubscribe Bars called ${listenerGuid}`);
        unsubscribe(listenerGuid)
    }

    //Subscribe depth to get the top 5 bids and asks of any symbol 
    subscribeDepth(symbol: string, callback: (a: MarketDepth) => void) {
        logMessage(`UdfCompatibleDatafeed: Subscribe Depth called ${symbol}`);
        var subscriberUID = `DOM-${symbol}-${Date.now()}`
        // Identity only — the DOM ladder streams bids/asks, it draws no candles.
        this.resolveSymbolInfo(symbol, (response) => {
            subscribeOnOdinStream([response], undefined, callback, subscriberUID, () => { }, undefined, 'depth')
        }, () => { }, '')
        return subscriberUID
    }

    //Unsubscribe the depth
    unsubscribeDepth(subscriberUid: string | undefined) {
        logMessage(`UdfCompatibleDatafeed: Unsubscribe Depth called ${subscriberUid}`);
        unsubscribe(String(subscriberUid))
    }

    getWatchLists() {
        const getWatchListsStartTime = Date.now();
        logMessage(`UdfCompatibleDatafeed: Get Watchlists called`);
        return this._tracer.startActiveSpan("GetWatchLists", async (span) => {
            return this._inHouseRequester.sendRequest(this._inHouseUrl, 'watchlist/stocks', {}, {}, "GET").then((response: any) => {
                span.end()
                return Promise.resolve(response)
            })
        })

        // logMessage(`UdfCompatibleDatafeed: Get Watchlists finished ${Date.now - getWatchListsStartTime}ms`);
    }
    //Get the symbolInfo from token
    async tokenInfo(token: TokenInfo) {
        return this._getSymbolFromTokens(token);
    }


    _send(urlPath: string, params = '') {
        return this._requester.sendRequest(this._inHouseUrl, urlPath, params);
    }


    //Call the odin apis
    _odinSend(urlPath: string, method = 'GET') {
        return this._odinRequester.sendRequest(this._odinUrl, urlPath, undefined, method);
    }


    //Call the flow-backend apis
    _inHouseSend(urlPath: string, params: any, body = {}, method = 'GET') {
        return this._inHouseRequester.sendRequest(this._inHouseUrl, urlPath, body, params, method)
    }


    //Set the configuration of scrips like its type,resolution etc.
    _setupWithConfiguration(configurationData: ChartDefaultConfiguration) {
        this._configuration = configurationData;
        if (!configurationData.exchanges) {
            configurationData.exchanges = [];
        }
        if (!configurationData.supports_search && !configurationData.supports_group_request) {
            throw new Error('Unsupported datafeed configuration. Must either support search, or support group request');
        }
        if (configurationData.supports_group_request || !configurationData.supports_search) {

        }
        logMessage(`UdfCompatibleDatafeed: Initialized with ${JSON.stringify(configurationData)}`);
    }

    //Get the token from scrip symbol
    async getTokensFromSymbol(symbols: string[]) {
        if (symbols.length == 1) {
            var cached: TokenInfo = this._symbolToTokenMap[symbols[0]]
            if (cached) {
                var response: TokenInfo[] = [{
                    symbol: symbols[0],
                    token: cached.token,
                    market_segment_id: cached.market_segment_id
                }]
                return Promise.resolve(response)
            }
        }
        return this._inHouseRequester.sendRequest(this._inHouseUrl, "symbolsToTokens", { symbols: symbols }, undefined, "POST").then((response: TokenInfo[]) => {
            response.forEach((element: TokenInfo) => {
                if (element.market_segment_id == 8) {
                    element.market_segment_id = 3
                }
                this._symbolToTokenMap[String(element.symbol)] = {
                    token: element.token,
                    market_segment_id: element.market_segment_id
                }
                // If the backend includes the CAS session here, take it. This is
                // the ONE bulk call the watchlist already makes for every symbol it
                // shows, so a session arriving in it costs nothing — no extra
                // request, no rate-limit budget, no pacing, however long the
                // watchlist is. Absent, behaviour is exactly as before: each row's
                // resolve looks its own session up.
                const extra = element as unknown as { session?: string; corrections?: string };
                if (element.token != null) {
                    this._session.seed(Number(element.market_segment_id), element.token, extra.session, extra.corrections);
                }
            });
            return Promise.resolve(response)
        })
    }

    // Pre-populate the token<->symbol caches from metadata already in hand
    // (e.g. the watchlists response, which carries StockData per stock). Mirrors
    // the webapp's ADVA-20 cache seeding: seed at data ingress so the existing
    // lookup paths (_getSymbolFromTokens / getTokensFromSymbol) become cache
    // hits — no call-site changes. Never overwrites an existing entry so live
    // resolution always wins.
    public seedTokenSymbol(info: { token: number | string; market_segment_id: number | string; symbol: string; description?: string; tick?: number; lotSize?: number; series?: string; isin?: string; hasIcon?: boolean }): boolean {
        if (!info || !info.token || !info.market_segment_id || !info.symbol) return false;
        const segment = Number(info.market_segment_id);
        const tokenNum = Number(info.token);
        const symbol = String(info.symbol);
        if (!isFinite(segment) || !isFinite(tokenNum)) return false;
        const tokenMap = this._tokenToSymbolMap[segment] ?? (this._tokenToSymbolMap[segment] = {});
        let seeded = false;
        if (tokenMap[tokenNum] === undefined) {
            tokenMap[tokenNum] = symbol;
            seeded = true;
        }
        if (this._symbolToTokenMap[symbol] === undefined) {
            this._symbolToTokenMap[symbol] = { token: tokenNum, market_segment_id: segment };
            seeded = true;
        }
        // Merge PER KEY, first value wins per field: a later, richer payload
        // (e.g. search after a sparse watchlist row) can fill missing fields
        // but never overwrites one already seeded. tick must arrive already
        // normalized to RUPEES.
        const meta = this._seededScripMeta[symbol] ?? (this._seededScripMeta[symbol] = {});
        if (meta.description === undefined && info.description) meta.description = String(info.description);
        if (meta.tick === undefined && typeof info.tick === 'number' && isFinite(info.tick) && info.tick > 0) meta.tick = info.tick;
        if (meta.lotSize === undefined && typeof info.lotSize === 'number' && isFinite(info.lotSize) && info.lotSize > 0) meta.lotSize = info.lotSize;
        if (meta.series === undefined && info.series) meta.series = String(info.series);
        if (meta.isin === undefined && info.isin) meta.isin = String(info.isin);
        if (meta.hasIcon === undefined && typeof info.hasIcon === 'boolean') meta.hasIcon = info.hasIcon;
        return seeded;
    }

    // Resolve MANY token/segment pairs with ONE bulk POST (aux/tokensToSymbols)
    // and seed the caches with the result. Response mapping is positional, same
    // contract as broker.helper's fetchAndMapTokensWithAuth. Used as a fallback
    // for ingress items that arrive without embedded symbol metadata.
    public async seedTokensBulk(items: { token: number | string; market_segment_id: number | string }[]): Promise<void> {
        const missing = (items || []).filter((it) =>
            it && it.token && it.market_segment_id &&
            !(this._tokenToSymbolMap[Number(it.market_segment_id)] && this._tokenToSymbolMap[Number(it.market_segment_id)][Number(it.token)])
        );
        if (missing.length === 0) return;
        const payload = {
            token_seg_ids: missing.map((it) => ({
                market_segment_id: String(it.market_segment_id),
                token: String(it.token),
            }))
        };
        return this._inHouseRequester.sendRequest(this._inHouseUrl, 'tokensToSymbols', payload, undefined, "POST").then((result: any) => {
            const rows = result && result.data;
            if (!Array.isArray(rows)) return;
            missing.forEach((it, index) => {
                const symbol = rows[index] && rows[index].symbol;
                if (symbol) {
                    this.seedTokenSymbol({ token: it.token, market_segment_id: it.market_segment_id, symbol: String(symbol) });
                }
            });
        });
    }

    // ----- CAS session (per-symbol, per-date) -------------------------------
    // Everything below delegates to SessionSource. Kept thin on purpose: the
    // rules (what is cached, what is retried, when we stop asking) are one
    // module away from the socket stream and the TV model layer, and are
    // covered by test/session-source.test.mjs.
    private _session: SessionSource = new SessionSource({
        post: (body, signal): Promise<SessionHttpMeta> =>
            // Minimal headers ONLY. Every extra header widens the CORS preflight's
            // Access-Control-Request-Headers, and this endpoint's gateway config
            // has been the narrowest of the lot. Content-Type and Authorization are
            // the two an authenticated JSON POST cannot do without.
            // (traceparent is excluded separately — it is injected by the OTel fetch
            // instrumentation, not by us. See services/otel/instrumentation.tsx.)
            this._inHouseRequester.sendRequestWithMeta(
                this._odinUrl, 'data/session_history', body, undefined, 'POST',
                this._inHouseRequester.minimalJsonHeaders(), signal
            ),
        override: readCasOverride(),
        log: (message: string) => logMessage(message),
    });

    // Start the session lookup for a symbol the chart is ABOUT to show, without
    // waiting on it, so the bounded wait inside resolveSymbol is normally already
    // satisfied by the time TV asks. Pure latency optimisation: the resolve path
    // fetches anyway, and this only fires when the token is already known (seeded
    // from the watchlist or global search).
    public prewarmSession(symbolName: string): void {
        try {
            if (!symbolName) return;
            const info = this._symbolToTokenMap && this._symbolToTokenMap[symbolName];
            if (!info || info.token == null || info.market_segment_id == null) return;
            this._session.prewarm(Number(info.market_segment_id), info.token);
        } catch (e) { /* best-effort prime */ }
    }

    // Resolve the session for `info`, then return a COPY carrying it. `requested`
    // is the symbol string TV asked with — the same string the container
    // registers, so the chart gate matches on identity rather than on whichever
    // field of the resolved payload happens to agree.
    private _withSessionHistory(info: ResolveSymbolInfo, requested?: string): Promise<ResolveSymbolInfo> {
        const segment = Number(info && info.odin_market_segment_id);
        const token = info && info.odin_token;
        if (!isCasSegment(segment) || token == null) return Promise.resolve(info);
        // EVERY segment-1/3 resolve gets the session — deliberately, including the
        // ones that are not for the chart.
        //
        // It is tempting to look this up only for the symbol the chart is showing:
        // this datafeed is shared with the order book, positions, baskets and the
        // chains, and most of what it resolves is never drawn as candles. That is
        // not possible. TV keeps a page-lifetime cache of resolved symbols:
        //   resolveSymbol(e,t,i){ if(this._symbolResolveMap.has(t)){ ...return cached }
        //                         ...this._symbolResolveMap.set(t,[e,Promise.resolve(i)]) }
        // cleared only on destroy()/connect(). So the FIRST resolve of a symbol is
        // the only one we are ever asked for, and it routinely happens before that
        // symbol is charted — TV's symbol search resolves what it previews, and the
        // watchlist resolves its rows. Hand back a symbolInfo without a session then,
        // and the chart is stuck with it until the page reloads. That is exactly the
        // "switching to SBIN keeps 15:30, reloading fixes it" bug.
        //
        // The cost is one POST per distinct cash-equity symbol per page: deduped,
        // cached forever after, one request (no retry) when the endpoint is unwell,
        // and none at all once the breaker opens.
        const name = String(requested || info.ticker || info.name || '');
        return this._session.waitFor(segment, token)
            .then((entry: SessionEntry | null) => {
                // Record what TV is actually being handed. If the session turns up
                // later, this is what tells us the chart is showing a stale one.
                this._session.noteHandedToTv(
                    segment, token, name, (entry && entry.session) || String(info.session || '')
                );
                return this._session.applyTo(info, entry);
            })
            .catch(() => info);
    }

    // One boot fetch of aux/symbol_segments → per-segment static config.
    // Safe to call repeatedly; refetches only if the cache is empty and no
    // fetch is in flight (so a failed boot fetch retries on next use).
    private _ensureSegmentInfo(): void {
        if (this._segmentInfoCache || this._segmentFetchInFlight) return;
        this._segmentFetchInFlight = this._inHouseRequester.sendRequest(this._inHouseUrl, 'symbol_segments', undefined, undefined, "GET")
            .then((response: any) => {
                if (response && response.segments) {
                    this._segmentInfoCache = response.segments;
                    logMessage(`UdfCompatibleDatafeed: symbol_segments loaded (${Object.keys(response.segments).length} segments)`);
                }
            })
            .catch((e: any) => {
                logMessage(`UdfCompatibleDatafeed: symbol_segments fetch failed: ${getErrorMessage(e)}`);
            })
            .finally(() => {
                this._segmentFetchInFlight = null;
            });
    }

    // CDN logo stem (port of mobile_charts __logoStem): derivatives share the
    // underlying's parent SVG; the IDX suffix is an internal index marker, not
    // part of the CDN filename.
    private _logoStemForSymbol(bareSymbol: string): string {
        let s = String(bareSymbol);
        if (/(CE|PE|FUT)$/.test(s)) {
            s = s.replace(/[0-9].*$/, '');
        }
        return s.replace(/IDX$/, '');
    }

    // logo_urls for the LOCAL build only. Server-resolved symbols keep the API's
    // own `logo_urls` untouched; this is used solely when we build a symbol
    // locally (no /aux/symbol call to read logo_urls from). It mirrors the CDN
    // convention already used across the app (see models/globalSearch.ts, which
    // builds the identical URL for search results): the scrip's own SVG when the
    // seed says it has an icon (has_icon), else the exchange logo — exactly what
    // the server returns for an icon-less scrip.
    private _localLogoUrls(bare: string, hasIcon: boolean | undefined, exchangeUrl: string): string[] {
        if (hasIcon === false) return [exchangeUrl];
        return [UDFCompatibleDatafeedBase.STOCK_LOGO_BASE + this._logoStemForSymbol(bare) + ".svg"];
    }

    // Derive TV price formatting from a RUPEE tick size. pricescale sets the
    // DISPLAYED decimals, so it is floored at the segment's display convention
    // (default_pricescale, e.g. 100 = 2 decimals on equities): a ₹0.10 tick
    // must render as { minmov: 10, pricescale: 100 } → "1400.10", not the
    // mathematically-minimal { minmov: 1, pricescale: 10 } → "1400.1".
    // Returns null when the tick doesn't convert cleanly (caller falls back to HTTP).
    private _priceScaleFromTick(tick: number, minPricescale: number): { minmov: number; pricescale: number } | null {
        if (!(typeof tick === 'number' && isFinite(tick) && tick > 0)) return null;
        const floor = (typeof minPricescale === 'number' && isFinite(minPricescale) && minPricescale >= 1) ? minPricescale : 100;
        for (let pricescale = 1; pricescale <= 10000000; pricescale *= 10) {
            const scaled = tick * pricescale;
            if (Math.abs(scaled - Math.round(scaled)) < 1e-6 && Math.round(scaled) >= 1) {
                const finalScale = Math.max(pricescale, floor);
                return { minmov: Math.round(tick * finalScale), pricescale: finalScale };
            }
        }
        return null;
    }

    // Build a FULL LibrarySymbolInfo locally — the last step of the
    // symbol_segments port, deliberately gated to CASH EQUITIES (segments
    // 1/NSE_EQ and 3/BSE_EQ, excluding *IDX index symbols):
    //   - segment-level truths (session, holidays, corrections, flags) come
    //     from the aux/symbol_segments cache — correct for every scrip in the
    //     segment by definition;
    //   - per-scrip truths (tick → minmov/pricescale, lot size, description,
    //     series, isin) come from seeded scrip metadata (watchlist StockData /
    //     global search) — NOT from segment defaults, which are wrong for any
    //     scrip whose tick deviates (order lines snap to this tick!);
    //   - F&O/MCX/currency are excluded because their symbolInfo feeds live
    //     order flows with odin_expiry_date/strike/option-type, which no seed
    //     source provides in the canonical format — they keep the (deduped,
    //     cached) /aux/symbol HTTP resolve;
    //   - *IDX symbols are excluded because isTradeable gates the order dialog
    //     and indices must keep the server's answer for it.
    // Returns null whenever ANY ingredient is missing — caller falls back to HTTP.
    private _buildSymbolInfoLocal(symbolName: string): ResolveSymbolInfo | null {
        const tok = this._symbolToTokenMap[symbolName];
        const meta = this._seededScripMeta[symbolName];
        if (!tok || !meta || tok.token == null || tok.market_segment_id == null) return null;
        const segment = Number(tok.market_segment_id);
        if (segment !== 1 && segment !== 3) return null;
        // NOTE on the segment blob as a session source: `segCfg.session` and
        // `segCfg.corrections` below are segment-UNIFORM, which is exactly what CAS
        // breaks. They survive here only as the LAST fallback, because
        // _withSessionHistory now applies data/session_history to every segment-1/3
        // resolve and that is authoritative. The blob is reached only when the
        // backend answers "no override for this scrip" — where the segment default
        // is correct by definition — or when the endpoint is unreachable, in which
        // case CAS is degraded regardless of which fallback we picked.
        const bare = symbolName.indexOf(':') !== -1 ? String(symbolName.split(':').pop()) : symbolName;
        if (/IDX$/.test(bare)) return null;
        this._ensureSegmentInfo();
        const segCfg = this._segmentInfoCache ? this._segmentInfoCache[String(segment)] : null;
        if (!segCfg) return null;
        const scale = this._priceScaleFromTick(Number(meta.tick), Number(segCfg.default_pricescale) || 100);
        if (!scale) return null;
        const info: ResolveSymbolInfo = {
            symbol: symbolName,
            ticker: symbolName,
            full_name: symbolName,
            // name must be EXCHANGE-QUALIFIED ("NSE:RELIANCE") — it drives the
            // header search box display; the bare form regressed it to "IDEA"
            // after a search-driven change.
            name: symbolName,
            description: meta.description || bare,
            exchange: segCfg.exchange,
            listed_exchange: segCfg.listed_exchange,
            type: segCfg.type || "stocks",
            session: segCfg.session,
            session_holidays: segCfg.session_holidays,
            corrections: segCfg.corrections,
            timezone: segCfg.timezone || "Asia/Kolkata",
            has_intraday: segCfg.has_intraday !== false,
            has_daily: segCfg.has_daily !== false,
            has_weekly_and_monthly: segCfg.has_weekly_and_monthly !== false,
            has_ticks: segCfg.has_ticks !== false,
            has_empty_bars: segCfg.has_empty_bars !== false,
            intraday_multipliers: segCfg.intraday_multipliers || ["1"],
            supported_resolutions: segCfg.supported_resolutions,
            data_status: segCfg.data_status || "streaming",
            minmov: scale.minmov,
            pricescale: scale.pricescale,
            visible_plots_set: segCfg.visible_plots_set || "ohlcv",
            isTradeable: true,
            odin_symbol: bare,
            odin_token: String(tok.token),
            odin_market_segment_id: segment,
            odin_exchange: segCfg.odin_exchange,
            odin_lot_size: meta.lotSize || 1,
            odin_series: meta.series || "",
            odin_expiry_date: "",
            isin: meta.isin || "",
            exchange_url: segCfg.exchange_url,
            // Local build only (server-resolved symbols keep the API's logo_urls).
            // Uses has_icon to pick the scrip's own SVG or the exchange logo —
            // matching the server, and avoiding a 404 that TV papers over by
            // keeping the PREVIOUS symbol's icon (stale-icon bug on symbol switch).
            logo_urls: this._localLogoUrls(bare, meta.hasIcon, segCfg.exchange_url),
        };
        return info;
    }

    async _getSymbolFromTokens(token: TokenInfo) {
        const segment = Number(token.market_segment_id);
        const tokenNum = Number(token.token);
        if (this._tokenToSymbolMap[segment] && this._tokenToSymbolMap[segment][tokenNum]) {
            return Promise.resolve({
                token: token.token,
                market_segment_id: token.market_segment_id,
                symbol: this._tokenToSymbolMap[segment][tokenNum]
            })
        }
        // Concurrent requests for the same token share one HTTP call.
        const inFlightKey = segment + "/" + tokenNum;
        const pending = this._tokenResolveInFlight[inFlightKey];
        if (pending) return pending;
        const request = this._inHouseRequester.sendRequest(this._inHouseUrl, 'tokenToSymbol/' + token.market_segment_id + "/" + token.token, undefined, undefined, "GET").then((response: TokenInfo) => {
            if (this._tokenToSymbolMap[segment] == undefined) {
                this._tokenToSymbolMap[segment] = {}
            }
            this._tokenToSymbolMap[segment][tokenNum] = String(response.symbol)
            return Promise.resolve(response)
        }).finally(() => {
            delete this._tokenResolveInFlight[inFlightKey];
        });
        this._tokenResolveInFlight[inFlightKey] = request;
        return request;
    }


}
function defaultConfiguration() {
    let data: ChartDefaultConfiguration = {
        supports_search: true,
        supports_group_request: true,
        has_seconds: true,
        has_ticks: true,
        supported_resolutions: [
            '5S',
            '15S',
            '30S',
            '1',
            '2',
            '3',
            '4',
            '5',
            '10',
            '15',
            '30',
            '45',
            '60',
            '120',
            '240',
            '1D',
            '1W',
            '1M',
        ],
        supports_marks: false,
        supports_timescale_marks: true,
        "exchanges": [
            {
                "value": "All Exchanges",
                "name": "All Exchanges",
                "desc": ""
            },
            {
                "value": "NSE",
                "name": "NSE",
                "desc": "National Stock Exchange "
            },
            {
                "value": "BSE",
                "name": "BSE",
                "desc": "Bombay Stock Exchange "
            },
            {
                "value": "MCX",
                "name": "MCX",
                "desc": "Multi Commodities Exchange"
            }
        ],
        // "exchanges": ["NSE","MCX"],
        "symbols_types": [{ "name": "All", "value": 5 }, { "name": "Stocks", "value": 1 }, { "name": "Options", "value": 2 }, { "name": "Futures", "value": 3 }, { "name": "Indices", "value": 4 }],
    };
    return data
}


// QA hook: ?cas_session=0915-1530%2320260730/0915-1515&cas_corrections=0915-1515:20260730
// forces a session so the post-CAS regime can be exercised before it goes live,
// without touching the device clock or the backend. Validated inside
// SessionSource like any other, so it cannot hang the chart. Never set in
// production.
function readCasOverride(): { session: string; corrections: string } | null {
    try {
        if (typeof window === 'undefined' || !window.location) return null;
        const params = new URLSearchParams(window.location.search);
        const session = params.get('cas_session') || '';
        if (!session) return null;
        return { session, corrections: params.get('cas_corrections') || '' };
    } catch (e) {
        return null;
    }
}
